import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Card } from 'primereact/card'
import { CheckboxChangeEvent } from 'primereact/checkbox'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../../components/Panel'
import {
  DropdownToggleable,
  InputNumberToggleable,
} from '../../../../../components/inputs'
import { OrderProcessBodyTemplate } from '../../../../../components/tableTemplates'
import {
  EditSupplierFormDto,
  InvoiceFormats,
  OrderProcessCompletions,
} from '../../data'
import { SupplierFormFieldName } from '../Supplier.data'

type Props = {
  isEdit: boolean
  isRequestRunning: boolean
  isLoading: boolean
  formik: FormikProps<EditSupplierFormDto>
  isFormFieldInvalid: (name: SupplierFormFieldName) => boolean
  getFormErrorMessage: (name: SupplierFormFieldName) => JSX.Element
  supplier?: EditSupplierFormDto
}

export const WebEdiOrderSettings: React.FC<Props> = ({
  isEdit,
  isRequestRunning,
  isLoading,
  formik,
  isFormFieldInvalid,
  getFormErrorMessage,
  supplier,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n

  const isProcessActiveOptions: { name: string; code: boolean }[] = [
    {
      name: t(
        'supplier.webEdiOrderSettings.form.activateOrders.options.active',
      ),
      code: true,
    },
    {
      name: t(
        'supplier.webEdiOrderSettings.form.activateOrders.options.deactivated',
      ),
      code: false,
    },
  ]

  const allowPreliminaryConfirmationOptions: { name: string; code: boolean }[] =
    [
      {
        name: t(
          'supplier.webEdiOrderSettings.form.activateOrders.options.active',
        ),
        code: true,
      },
      {
        name: t(
          'supplier.webEdiOrderSettings.form.activateOrders.options.deactivated',
        ),
        code: false,
      },
    ]

  const orderProcessCompletionsoptions: OrderProcessCompletions[] = [
    'orderresponse',
    'despatchadvice',
    'invoice',
  ]

  const invoiceFormatOptions: {
    name: string
    code: InvoiceFormats
  }[] = [
    {
      name: t(
        'supplier.webEdiOrderSettings.form.invoiceFormat.options.sap_idoc',
      ),
      code: 'sap_idoc',
    },
    {
      name: t(
        'supplier.webEdiOrderSettings.form.invoiceFormat.options.joubini_ibi',
      ),
      code: 'joubini_ibi',
    },
    {
      name: t(
        'supplier.webEdiOrderSettings.form.invoiceFormat.options.zugferd',
      ),
      code: 'zugferd',
    },
    {
      name: t(
        'supplier.webEdiOrderSettings.form.invoiceFormat.options.insiders',
      ),
      code: 'insiders',
    },
    {
      name: t('supplier.webEdiOrderSettings.form.invoiceFormat.options.custom'),
      code: 'custom',
    },
  ]

  const orderProcessTemplate = (option: OrderProcessCompletions) => {
    return OrderProcessBodyTemplate(option)
  }

  return (
    <PanelContainer
      header={t('supplier.webEdiOrderSettings.title')}
      id="supplier-webEdiOrderSettings"
      toggleable={false}
    >
      <div className="grid">
        <div className="col-12 lg:col-4">
          <p>{t('supplier.webEdiOrderSettings.subtitle')}</p>
        </div>
        <div className="col-12 lg:col-7 lg:col-offset-1">
          <Card className="md:px-4">
            <div className="formgrid grid">
              {/* OrderIsProcessActive */}
              <div className="col-12 md:col-6 mb-3">
                <label
                  className="text-xs font-bold block mb-2"
                  htmlFor="orderIsProcessActiveValue"
                >
                  {t('supplier.webEdiOrderSettings.form.activateOrders.label')}
                </label>

                <DropdownToggleable
                  checked={formik.values.orderIsProcessActiveOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'orderIsProcessActiveOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'supplier.webEdiOrderSettings.form.activateOrders.checkboxTooltip',
                  )}
                  id="orderIsProcessActiveValue"
                  options={isProcessActiveOptions}
                  optionLabel="name"
                  optionValue="code"
                  value={formik.values.orderIsProcessActiveValue}
                  onChange={formik.handleChange}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid(
                      'orderIsProcessActiveValue',
                    ),
                  })}
                  disabled={!isEdit || isRequestRunning}
                />
              </div>

              {/* processCompletions */}
              <div className="col-12 md:col-6 mb-3">
                <label
                  className="text-xs font-bold block mb-2"
                  htmlFor="orderProcessCompletionsValue"
                >
                  {t(
                    'supplier.webEdiOrderSettings.form.processCompletions.label',
                  )}
                </label>

                <DropdownToggleable
                  checked={formik.values.orderProcessCompletionsOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'orderProcessCompletionsOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'supplier.webEdiOrderSettings.form.activateOrders.checkboxTooltip',
                  )}
                  id="orderProcessCompletionsValue"
                  options={orderProcessCompletionsoptions}
                  value={formik.values.orderProcessCompletionsValue}
                  onChange={formik.handleChange}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid(
                      'orderProcessCompletionsValue',
                    ),
                  })}
                  valueTemplate={orderProcessTemplate}
                  itemTemplate={orderProcessTemplate}
                  disabled={!isEdit || isRequestRunning}
                />
              </div>

              {/* OrderResponsePeriod */}
              <div className="col-12 md:col-6 mb-3">
                <label
                  className={classNames('text-xs font-bold block mb-2', {
                    'p-error': isFormFieldInvalid('orderResponsePeriodValue'),
                  })}
                  htmlFor="orderResponsePeriodValue"
                >
                  {t('supplier.webEdiOrderSettings.form.responsePeriod.label')}
                </label>
                <InputNumberToggleable
                  checked={formik.values.orderResponsePeriodOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'orderResponsePeriodOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'supplier.emailNotifications.form.language.checkboxTooltip',
                  )}
                  id="orderResponsePeriodValue"
                  value={formik.values.orderResponsePeriodValue}
                  onValueChange={formik.handleChange}
                  min={1}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid('orderResponsePeriodValue'),
                  })}
                  suffix={t('common.suffix.days', {
                    count: formik.values.orderResponsePeriodValue,
                  })}
                  showButtons
                  locale={language}
                  disabled={!isEdit || isRequestRunning}
                />
              </div>

              {/* OrderDeliveryDateDeviation */}
              <div className="col-12 md:col-6 mb-3">
                <label
                  className={classNames('text-xs font-bold block mb-2', {
                    'p-error': isFormFieldInvalid(
                      'orderDeliveryDateDeviationValue',
                    ),
                  })}
                  htmlFor="orderDeliveryDateDeviationValue"
                >
                  {t(
                    'supplier.webEdiOrderSettings.form.deliveryDateDeviation.label',
                  )}
                </label>
                <InputNumberToggleable
                  checked={formik.values.orderDeliveryDateDeviationOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'orderDeliveryDateDeviationOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'supplier.emailNotifications.form.language.checkboxTooltip',
                  )}
                  id="orderDeliveryDateDeviationValue"
                  value={formik.values.orderDeliveryDateDeviationValue}
                  onValueChange={formik.handleChange}
                  min={0}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid(
                      'orderDeliveryDateDeviationValue',
                    ),
                  })}
                  showButtons
                  suffix={
                    formik.values.orderDeliveryDateDeviationValue
                      ? t('common.suffix.days', {
                          count: formik.values.orderDeliveryDateDeviationValue,
                        })
                      : undefined
                  }
                  tooltip={t(
                    'supplier.webEdiOrderSettings.form.deliveryDateDeviation.hint',
                  )}
                  tooltipOptions={{ position: 'bottom' }}
                  locale={language}
                  disabled={!isEdit || isRequestRunning}
                />
              </div>

              {/* InvoiceFormat */}
              <div className="col-12 md:col-6 mb-3">
                <label
                  className="text-xs font-bold block mb-2"
                  htmlFor="orderInvoiceFormatValue"
                >
                  {t('supplier.webEdiOrderSettings.form.invoiceFormat.label')}
                </label>

                <DropdownToggleable
                  checked={formik.values.orderInvoiceFormatOverride}
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'orderInvoiceFormatOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'supplier.webEdiOrderSettings.form.invoiceFormat.checkboxTooltip',
                  )}
                  id="orderInvoiceFormatValue"
                  options={invoiceFormatOptions}
                  optionLabel="name"
                  optionValue="code"
                  value={formik.values.orderInvoiceFormatValue}
                  onChange={formik.handleChange}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid('orderInvoiceFormatValue'),
                  })}
                  disabled={!isEdit || isRequestRunning}
                />
              </div>

              {/* Allow preliminary confirmation */}
              <div className="col-12 md:col-6 mb-3">
                <label
                  className="text-xs font-bold block mb-2"
                  htmlFor="orderAllowPreliminaryConfirmationValue"
                >
                  {t(
                    'supplier.webEdiOrderSettings.form.allowPreliminaryConfirmation.label',
                  )}
                </label>

                <DropdownToggleable
                  checked={
                    formik.values.orderAllowPreliminaryConfirmationOverride
                  }
                  onCheckedChange={(e: CheckboxChangeEvent) => {
                    formik.setFieldValue(
                      'orderAllowPreliminaryConfirmationOverride',
                      e.checked,
                    )
                  }}
                  checkboxTooltip={t(
                    'supplier.webEdiOrderSettings.form.allowPreliminaryConfirmation.checkboxTooltip',
                  )}
                  id="orderAllowPreliminaryConfirmationValue"
                  options={allowPreliminaryConfirmationOptions}
                  optionLabel="name"
                  optionValue="code"
                  value={formik.values.orderAllowPreliminaryConfirmationValue}
                  onChange={formik.handleChange}
                  className={classNames('w-full', {
                    'p-invalid': isFormFieldInvalid(
                      'orderAllowPreliminaryConfirmationValue',
                    ),
                  })}
                  disabled={!isEdit || isRequestRunning}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </PanelContainer>
  )
}
